/*
 * A loop drawn around a mushroom on the live camera, and the part of the
 * picture it names (docs/FIELD_AI_VISION_SPEC.md section 13, the encircle).
 * Pure: the camera hands over the loop in viewport px (utils/gestures/
 * recognise.js) and the frame's size; this says what to crop, where the loop
 * is drawn and which side of the screen the panel takes.
 *
 * The picture is shown with object-fit cover, so the viewport is a window on
 * the middle of the frame, scaled up: a point on the screen maps to the frame
 * by that scale and the hidden margins. The box is padded by ENCIRCLE.pad of
 * its own size (a loop drawn tight still shows the whole cap), kept inside the
 * frame, and never smaller than ENCIRCLE.minSide frame px.
 */
export const ENCIRCLE = Object.freeze({ pad: 0.15, cropMax: 1024, minSide: 32 });

/** The loop's bounding box in its own px: {left, top, width, height}, or null for no points. */
export function loopBounds(loop) {
  if (!loop?.length) return null;
  const xs = loop.map(p => p.x);
  const ys = loop.map(p => p.y);
  const left = Math.min(...xs);
  const top = Math.min(...ys);
  return { left, top, width: Math.max(...xs) - left, height: Math.max(...ys) - top };
}

/**
 * The frame px a box on the viewport covers: {x, y, w, h} (whole px), for a
 * frame of `frame` {width, height} shown in `viewport` {width, height} with
 * object-fit cover. Padded by `pad` and kept inside the frame; null when the
 * box misses the frame or any size is missing.
 */
export function frameRect(bounds, viewport, frame, { pad = ENCIRCLE.pad, minSide = ENCIRCLE.minSide } = {}) {
  if (!bounds || !(viewport?.width > 0) || !(viewport?.height > 0) || !(frame?.width > 0) || !(frame?.height > 0)) {
    return null;
  }
  const scale = Math.max(viewport.width / frame.width, viewport.height / frame.height);
  const offsetX = (frame.width * scale - viewport.width) / 2;
  const offsetY = (frame.height * scale - viewport.height) / 2;
  const toFrame = (vx, vy) => [(vx + offsetX) / scale, (vy + offsetY) / scale];
  const padX = bounds.width * pad;
  const padY = bounds.height * pad;
  const [x0, y0] = toFrame(bounds.left - padX, bounds.top - padY);
  const [x1, y1] = toFrame(bounds.left + bounds.width + padX, bounds.top + bounds.height + padY);
  let left = Math.max(0, Math.floor(x0));
  let top = Math.max(0, Math.floor(y0));
  let right = Math.min(frame.width, Math.ceil(x1));
  let bottom = Math.min(frame.height, Math.ceil(y1));
  if (right <= left || bottom <= top) return null;
  // Too small to say anything about: grown about its middle, inside the frame.
  const grow = (lo, hi, size) => {
    if (hi - lo >= minSide || size < minSide) return [lo, hi];
    const mid = (lo + hi) / 2;
    const start = Math.min(Math.max(0, Math.round(mid - minSide / 2)), size - minSide);
    return [start, start + minSide];
  };
  [left, right] = grow(left, right, frame.width);
  [top, bottom] = grow(top, bottom, frame.height);
  return { x: left, y: top, w: right - left, h: bottom - top };
}

/** The size a crop of {w, h} is encoded at: its longer side at most `max`. */
export function cropSize({ w, h }, max = ENCIRCLE.cropMax) {
  const scale = Math.min(1, max / Math.max(w, h));
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
}

/** The loop as an SVG path ("M x y L ... Z"), to one decimal. */
export function loopPath(loop) {
  if (!loop?.length) return '';
  const r = v => Math.round(v * 10) / 10;
  return `M${loop.map(p => `${r(p.x)} ${r(p.y)}`).join(' L')} Z`;
}

/** Where the floating panel goes: 'bottom' for a loop in the upper half of the screen, else 'top'. */
export function panelSide(bounds, viewport) {
  if (!bounds || !(viewport?.height > 0)) return 'bottom';
  return bounds.top + bounds.height / 2 < viewport.height / 2 ? 'bottom' : 'top';
}

/**
 * A loop for a person who cannot draw one (WCAG 2.5.1: a path gesture has a
 * single-pointer alternative): a circle of `share` of the shorter side, in
 * the middle of the viewport, as recognise.js would hand over a drawn one.
 */
export function centreLoop(viewport, { share = 0.55, points = 24 } = {}) {
  const cx = viewport.width / 2;
  const cy = viewport.height / 2;
  const radius = (Math.min(viewport.width, viewport.height) * share) / 2;
  return Array.from({ length: points }, (_, i) => {
    const a = (2 * Math.PI * i) / points;
    return { x: cx + radius * Math.cos(a), y: cy + radius * Math.sin(a) };
  });
}
